// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { ExtractedDocumentKind } from '../types/extraction';
import type { LoadedPdf } from './pdfTextLoader';
import { fullText } from './pdfTextLoader';

export interface FormatDetectionResult {
  kind: ExtractedDocumentKind;
  /** Set when the provider is recognised but the body text is not extractable. */
  imageBasedWarning?: string;
}

/**
 * Inspect the first couple of pages and decide which extractor to run.
 *
 * Fingerprints follow `docs/features/provider-form/standardlife-statement-extraction.md`:
 *  - both Standard Life PDFs carry the `standardlife.ie` brand and a
 *    `Synergy` product token;
 *  - the annual statement uniquely contains
 *    `Statement of reasonable projection`;
 *  - the transactions statement uniquely contains
 *    `Understanding your transactions` (and lacks the projection section).
 *
 * Some Standard Life PDFs (e.g. password-removed copies) have their body
 * content rendered as images with only the footer as extractable text.
 * When we detect the Standard Life brand but cannot find the expected
 * product / section tokens, we check whether the PDF is image-based and
 * return a targeted warning so the user knows why extraction failed.
 */
export function detectFormat(pdf: LoadedPdf): FormatDetectionResult {
  const headerText = pdf.pages.slice(0, 2).map((p) => p.text).join('\n').toLowerCase();
  const allText = fullText(pdf).toLowerCase();

  const isStandardLife =
    headerText.includes('standardlife.ie') ||
    headerText.includes('standard life') ||
    allText.includes('standardlife.ie');
  if (!isStandardLife) return { kind: 'UNKNOWN' };

  const looksLikeSynergy = allText.includes('synergy');

  if (looksLikeSynergy) {
    if (allText.includes('statement of reasonable projection')) {
      return { kind: 'STANDARD_LIFE_ANNUAL' };
    }
    if (
      allText.includes('understanding your transactions') ||
      allText.includes('payments into your policy')
    ) {
      return { kind: 'STANDARD_LIFE_TRANSACTIONS' };
    }
  }

  // Brand detected but body tokens missing — check if the PDF is
  // image-based (very little text relative to page count).
  const avgCharsPerPage = allText.length / Math.max(1, pdf.numPages);
  if (avgCharsPerPage < 200) {
    return {
      kind: 'UNKNOWN',
      imageBasedWarning:
        'Standard Life statement detected, but the document body appears to be ' +
        'image-based (e.g. a scanned or password-removed copy). Auto-extraction ' +
        'requires a digitally-generated PDF. Please request a text-based ' +
        'statement from Standard Life, or continue filling the form manually.',
    };
  }

  return { kind: 'UNKNOWN' };
}

/** Cheap stable fingerprint based on policy number + length, used for diffing. */
export function fingerprintDocument(pdf: LoadedPdf): string {
  const text = fullText(pdf);
  const policyNumberMatch = text.match(/Policy number[:\s]+([A-Z0-9-]+)/i);
  const policy = policyNumberMatch ? policyNumberMatch[1] : 'unknown';
  return `${policy}:${pdf.numPages}:${text.length}`;
}
